(ns practitest.frontend.components2.dashboard.charts.bar-render
  (:require ["d3" :as d3]))

(defn get-element-dimensions
  "Get viewbox dimensions from SVG element's actual size."
  [svg-element]
  (let [rect (when svg-element (.getBoundingClientRect svg-element))]
    {:viewbox-width  (some-> rect .-width)
     :viewbox-height (some-> rect .-height)}))

(defn- ensure-group!
  "Reuse existing <g> by class name, or append a new one. Preserves DOM across re-renders for D3 transitions."
  ([svg class-name] (ensure-group! svg class-name nil))
  ([svg class-name transform]
   (let [existing (.select svg (str "g." class-name))
         g        (if (.empty existing)
                    (-> svg (.append "g") (.attr "class" class-name))
                    existing)]
     (cond-> g transform (.attr "transform" transform)))))

(defn setup-svg!
  "Initialize SVG element with margins. Reuses existing chart group for transitions."
  [svg-element {:keys [viewbox-width viewbox-height margin]}]
  (.setAttribute svg-element "viewBox" (str "0 0 " viewbox-width " " viewbox-height))
  (let [transform (str "translate(" (:left margin) "," (:top margin) ")")
        g         (ensure-group! (.select d3 svg-element) "chart-group")]
    (.attr g "transform" transform)))

(defn calc-inner-dimensions
  "Calculate inner chart dimensions (without margins)."
  [viewbox-width viewbox-height {:keys [margin]}]
  {:inner-width  (- viewbox-width (:left margin) (:right margin))
   :inner-height (- viewbox-height (:top margin) (:bottom margin))})

;; ============================================================================
;; Scales
;; ============================================================================

(defn create-x-scale
  "Create band scale for category groups."
  [categories inner-width group-gap group-outer-gap]
  (-> d3
      (.scaleBand)
      (.domain (clj->js categories))
      (.range #js [0 inner-width])
      (.paddingInner group-gap)
      (.paddingOuter group-outer-gap)))

(defn- create-x-subgroup-scale
  "Create band scale for bars within each group."
  [series-names x-bandwidth bar-gap]
  (-> d3
      (.scaleBand)
      (.domain (clj->js series-names))
      (.range #js [0 x-bandwidth])
      (.padding bar-gap)))

(defn create-y-scale
  "Create linear scale for values."
  [max-value inner-height y-headroom]
  (-> d3
      (.scaleLinear)
      (.domain #js [0 (* max-value y-headroom)])
      (.range #js [inner-height 0])))


;; ============================================================================
;; Renderers
;; ============================================================================

(defn- render-x-axis!
  "Render x-axis with foreignObject labels for CSS text truncation.
   Replaces D3 axisBottom with a manual baseline + foreignObject per category."
  [svg {:keys [update-duration margin tick-length]} {:keys [x-scale inner-height inner-width]}]
  (let [g          (ensure-group! svg "x-axis" (str "translate(0," inner-height ")"))
        bw         (.bandwidth x-scale)
        half-bw    (/ bw 2)
        fo-height  (:bottom margin)
        categories (js->clj (.domain x-scale))

        ;; Baseline — ensure exactly one <line> exists, then update width
        baseline   (let [sel     (-> g (.selectAll "line.baseline") (.data #js [nil]))
                         entered (-> (.enter sel)
                                     (.append "line")
                                     (.attr "class" "baseline")
                                     (.attr "y1" 0)
                                     (.attr "y2" 0)
                                     (.attr "stroke" "currentColor"))]
                     (.merge entered sel))
        _          (.attr baseline "x2" inner-width)

        ;; Label groups — one per category
        labels     (-> g
                       (.selectAll "g.x-label")
                       (.data (clj->js categories) (fn [^js d] d)))
        entered    (-> (.enter labels)
                       (.append "g")
                       (.attr "class" "x-label")
                       (.attr "transform" (fn [^js d] (str "translate(" (x-scale d) ",0)"))))
        _          (.remove (.exit labels))]

    ;; ENTER: append tick + foreignObject + HTML div
    (-> entered
        (.append "line")
        (.attr "class" "tick")
        (.attr "x1" half-bw)
        (.attr "y1" 0)
        (.attr "x2" half-bw)
        (.attr "y2" tick-length)
        (.attr "stroke" "currentColor"))

    (-> entered
        (.append "foreignObject")
        (.attr "width" bw)
        (.attr "height" fo-height)
        (.attr "x" 0)
        (.attr "y" tick-length)
        (.append "xhtml:div")
        (.attr "class" "label-content")
        (.text (fn [^js d] d)))

    ;; UPDATE: transition position + refresh width/text/tick
    (-> labels
        (.transition)
        (.duration update-duration)
        (.attr "transform" (fn [^js d] (str "translate(" (x-scale d) ",0)"))))

    (-> labels
        (.select "line.tick")
        (.attr "x1" half-bw)
        (.attr "x2" half-bw))

    (-> labels
        (.select "foreignObject")
        (.attr "width" bw))

    (-> labels
        (.select ".label-content")
        (.text (fn [^js d] d)))))

(defn- render-y-axis!
  "Render y-axis. Reuses existing axis group."
  [svg {:keys [update-duration]} {:keys [y-scale]}]
  (-> (ensure-group! svg "y-axis")
      (.transition)
      (.duration update-duration)
      (.call (.axisLeft d3 y-scale))))

(defn- render-bars!
  "Render grouped bars with enter/exit/update transitions."
  [svg {:keys [exit-duration update-duration enter-duration
               ease-exit ease-update ease-enter enter-width-ratio on-bar-click]}
   {:keys [data-ready x-scale x-subgroup-scale y-scale inner-height
           tooltip]}]
  (let [cat-key  (fn [^js d] (.-category d))
        grp-key  (fn [^js d] (.-group d))
        x-fn     (fn [^js d] (x-subgroup-scale (.-group d)))
        y-fn     (fn [^js d] (y-scale (.-value d)))
        h-fn     (fn [^js d] (- inner-height (y-scale (.-value d))))
        fill-fn  (fn [^js d] (.-color d))
        bw       (.bandwidth x-subgroup-scale)
        groups   (-> (ensure-group! svg "bars")
                     (.selectAll "g.bar-group")
                     (.data data-ready cat-key))
        entered  (-> (.enter groups)
                     (.append "g")
                     (.attr "class" "bar-group"))
        ;; Combine new + existing category groups into one selection
        merged   (.merge entered groups)]

    ;; EXIT groups
    (-> (.exit groups)
        (.transition)
        (.duration exit-duration)
        (.style "opacity" 0)
        (.remove))

    ;; MERGE enter + update groups — transition position
    (-> merged
        (.transition)
        (.delay exit-duration)
        (.duration update-duration)
        (.ease ease-update)
        (.attr "transform" (fn [^js d] (str "translate(" (x-scale (.-category d)) ",0)"))))

    ;; Handle bars within each group
    (.each merged
      (fn [^js _d]
        (this-as this
          (let [g        (.select d3 this)
                rects    (-> g
                             (.selectAll "rect.bar-rect")
                             (.data (fn [^js d] (.-values d)) grp-key))
                enter    (.enter rects)
                exit     (.exit rects)
                enter-w  (* bw enter-width-ratio)
                enter-ox (/ (- bw enter-w) 2)
                new-bars (-> enter
                             (.append "rect")
                             (.attr "class" "bar-rect")
                             (.attr "x" (fn [^js d] (+ (x-fn d) enter-ox)))
                             (.attr "y" inner-height)
                             (.attr "width" enter-w)
                             (.attr "height" 0)
                             (.attr "fill" fill-fn))
                all      (.merge new-bars rects)]

            ;; Phase 1 — EXIT: shrink to bottom
            (-> exit
                (.transition)
                (.duration exit-duration)
                (.ease ease-exit)
                (.attr "y" inner-height)
                (.attr "height" 0)
                (.remove))

            ;; Phase 2 — UPDATE: reposition after exit completes
            (-> rects
                (.transition)
                (.delay exit-duration)
                (.duration update-duration)
                (.ease ease-update)
                (.attr "x" x-fn)
                (.attr "y" y-fn)
                (.attr "width" bw)
                (.attr "height" h-fn)
                (.attr "fill" fill-fn))

            ;; Phase 3 — ENTER: grow after update completes
            (-> new-bars
                (.transition)
                (.delay (+ exit-duration update-duration))
                (.duration enter-duration)
                (.ease ease-enter)
                (.attr "x" x-fn)
                (.attr "width" bw)
                (.attr "y" y-fn)
                (.attr "height" h-fn))

            ;; Events on merged selection
            (when tooltip
              (-> all
                  (.on "mouseenter"
                       (fn [^js _event ^js d]
                         (this-as el
                           (reset! tooltip {:text    (str (.-group d) ": " (.-value d))
                                            :element el}))))
                  (.on "mouseleave"
                       (fn [] (reset! tooltip nil)))))
            (when on-bar-click
              (.on all "click"
                   (fn [^js _event ^js d]
                     (on-bar-click (js->clj d :keywordize-keys true)))))))))))


(defn render-legend!
  "Render legend below the chart within the same SVG."
  [svg {:keys [legend-bar-height legend-bar-gap legend-label-height update-duration on-legend-click]}
   {:keys [legend-items inner-width inner-height legend-top-offset]}]
  (let [n          (count legend-items)
        item-width (/ inner-width n)
        bar-width  (- item-width legend-bar-gap)
        legend-y   (+ inner-height legend-top-offset)
        legend-g   (ensure-group! svg "legend" (str "translate(0," legend-y ")"))
        lbl-key    (fn [^js d] (.-label d))
        active-fn  (fn [^js d] (aget d "is-active"))
        class-fn   (fn [^js d] (str "legend-item" (when (active-fn d) " active")))
        items      (-> legend-g
                       (.selectAll "g.legend-item")
                       (.data (clj->js legend-items) lbl-key))
        entered    (-> (.enter items)
                       (.append "g")
                       (.attr "class" class-fn)
                       (.attr "transform" (fn [_ i] (str "translate(" (* i item-width) ",0)")))
                       (.each (fn [^js d]
                                (this-as this
                                  (let [g (.select d3 this)]
                                    (-> g
                                        (.append "rect")
                                        (.attr "x" 0)
                                        (.attr "y" 0)
                                        (.attr "width" bar-width)
                                        (.attr "height" legend-bar-height)
                                        (.attr "fill" (.-color d)))
                                    (-> g
                                        (.append "foreignObject")
                                        (.attr "x" 0)
                                        (.attr "y" legend-bar-height)
                                        (.attr "width" bar-width)
                                        (.attr "height" legend-label-height)
                                        (.append "xhtml:div")
                                        (.attr "class" "legend-label")
                                        (.text (.-label d))))))))
        all        (.merge entered items)]

    ;; EXIT
    (-> (.exit items)
        (.transition)
        (.duration update-duration)
        (.style "opacity" 0)
        (.remove))

    ;; UPDATE: transition class, position, and refresh width/text
    (-> items
        (.attr "class" class-fn)
        (.transition)
        (.duration update-duration)
        (.attr "transform" (fn [_ i] (str "translate(" (* i item-width) ",0)"))))

    (-> items
        (.select "foreignObject")
        (.attr "width" bar-width))

    (-> items
        (.select ".legend-label")
        (.text (fn [^js d] (.-label d))))

    ;; Events on merged selection
    (when on-legend-click
      (.on all "click" (fn [^js _event ^js d]
                         (on-legend-click (.-label d)))))))

;; ============================================================================
;; Main
;; ============================================================================

(defn render-chart!
  "Main render function for grouped bar chart."
  [svg-element {:chart/keys [legend max-value data categories series-labels]} config]
  (let [{:keys [viewbox-width viewbox-height]} (get-element-dimensions svg-element)
        viewbox-width                          (or viewbox-width  (:default-viewbox-width config))
        viewbox-height                         (or viewbox-height (:default-viewbox-height config))]
    (when (and (seq data) (pos? viewbox-width) (pos? viewbox-height))
      (let [svg           (setup-svg! svg-element {:viewbox-width  viewbox-width
                                                   :viewbox-height viewbox-height
                                                   :margin         (:margin config)})
            chart-height  (* viewbox-height (:chart-ratio config))
            legend-height (* viewbox-height (:legend-ratio config))

            {:keys [inner-width
                    inner-height]} (calc-inner-dimensions viewbox-width chart-height config)

            x-scale          (create-x-scale categories inner-width (:group-gap config) (:group-outer-gap config))
            x-subgroup-scale (create-x-subgroup-scale series-labels (.bandwidth x-scale) (:bar-gap config))
            y-scale          (create-y-scale max-value inner-height (:y-headroom config))
            ctx              {:data-ready        (clj->js data)
                              :x-scale           x-scale
                              :x-subgroup-scale  x-subgroup-scale
                              :y-scale           y-scale
                              :inner-width       inner-width
                              :inner-height      inner-height
                              :legend-items      legend
                              :legend-top-offset (* legend-height (:legend-offset-ratio config))
                              :tooltip           (:tooltip config)}]
        (render-x-axis! svg config ctx)
        (render-y-axis! svg config ctx)
        (render-bars! svg config ctx)
        (render-legend! svg config ctx)))))
